import React, { memo, useMemo, useState } from "react";
import styles from "./OneTimePasswordForm.module.scss";
import AuthentificationService from "../../../../httpService/AuthentificationService";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { logIn } from "../../../../stores/slices/current-user-slice";
import { useLocation, useNavigate } from "react-router-dom";
import { ApplicationRoutes } from "../../../../routes";
import { BounceLoader } from "react-spinners";
import OtpInput from "react-otp-input";
import { UserType } from "../../../../model/userType";
import TimerButton from "../../../../common/components/timer-button/TimerButton";
import { useLazyGetCustomerInfoQuery } from "../../../../service/customer-service";

type Props = {
	email: string;
	onChangeEmail: () => void;
};

const CheckoutOneTimePasswordForm: React.FC<Props> = memo((props: Props) => {
	const location = useLocation();
	const [getCustomerInfo] = useLazyGetCustomerInfoQuery();
	const goBackRoute: string | undefined = location.state ? location.state.route : null;

	const otpLength = 6;
	const [errorMessage, setErrorMessage] = useState("");

	const { email, onChangeEmail } = props;

	const authentificationService = useMemo(() => new AuthentificationService(), []);

	const dispatch = useDispatch();
	const navigate = useNavigate();

	const [otp, setOtp] = useState("");
	const [loading, setLoading] = useState(false);

	const [isSubmitted, setIsSubmitted] = useState<boolean>(false);

	const validateOtp = (otp: string) => {
		if (otp.length === otpLength) {
			setErrorMessage("");
			return true;
		} else {
			setErrorMessage("Please enter the complete OTP");
			return false;
		}
	};

	const handleChange = (otp: string) => {
		setOtp(otp);
		if (isSubmitted) {
			validateOtp(otp);
		}
	};

	const handleVerifyButton = () => {
		handleVerify(otp);
	};

	const handleResendOtp = async () => {
		try {
			await authentificationService.resendOtp(email);

			toast.success("OTP sent successfully");
		} catch (error: any) {
			toast.error(error.data?.title || "Failed to resend OTP. Please try again.");
		}
	};

	const handleVerify = async (otp: string) => {
		try {
			setIsSubmitted(true);
			if (!validateOtp(otp)) {
				return;
			}
			setLoading(true);
			const response = await authentificationService.verifyOtp(email, otp);
			getCustomerInfo(undefined, true);
			dispatch(
				logIn({
					token: response.data.token,
					userType: response.data.userType,
					refreshToken: response.data.refreshToken,
				})
			);

			const userType = response.data.userType;
			let route = "";
			switch (userType) {
				case UserType.Customer:
					navigate(ApplicationRoutes.BILLING_INFO);
					break;
			}
			navigate(goBackRoute ?? route);
		} catch (error: any) {
			toast.error(error.response?.data?.title || "Failed to validate OTP. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className={`${styles.container} ${styles.checkoutOneTimeContainer}`}>
			<div className={styles.header}>
				OTP
				<div className={`link`}>Need Help?</div>
			</div>
			<div className={styles.contentContainer}>
				<div className={styles.inputLabel}>
					Enter the code we've sent by mail to {email}
					<button className={`standardButton ${styles.changeEmailButton}`} onClick={onChangeEmail}>
						Change mail
					</button>
				</div>
				<OtpInput value={otp} onChange={handleChange} numInputs={otpLength} containerStyle={styles.otpContainer} renderInput={props => <input {...props} />} />
				{errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
				<button disabled={loading} className={`standardButton ${styles.verifyButton}`} onClick={handleVerifyButton}>
					{loading ? <BounceLoader color="#DE841B" size={25} /> : "Verify"}
				</button>
				<p><b>Important: </b>If you do not receive the email in your inbox, please check your spam or junk folder as it may have been filtered by your email provider.</p>
				<div className={styles.resendContainer}>
					Didn't receive the code? <TimerButton timerLength={60} onClick={handleResendOtp} />
				</div>
			</div>
		</div>
	);
});

export default CheckoutOneTimePasswordForm;
